export function createStreamLifecycleController({
  elements, onStreamStateChange, stopActiveStream, sessionStore, artifactFacade, runFacade,
  getLastPersistedMessageId, syncMessagesAfter, updateTokenCounter, publishAgentEvent,
  onSyncError = null,
  documentRef = document, windowRef = window, now = () => Date.now(),
  setTimeoutFn = setTimeout, clearTimeoutFn = clearTimeout,
}) {
  const { sendButton, stopButton } = elements;
  let streaming = false;
  let lastResyncAt = 0;
  let lastSyncErrorNotifiedAt = 0;
  let timer = null;
  let resyncController = null;
  let unsubscribe = null;
  let bound = false;

  function cancelResync() {
    if (timer !== null) clearTimeoutFn(timer);
    timer = null;
    resyncController?.abort();
    resyncController = null;
  }

  async function runResync() {
    timer = null;
    if (streaming || documentRef.visibilityState === 'hidden') return;
    const session = sessionStore.getSessionState().currentSession;
    const lastId = getLastPersistedMessageId();
    if (!session?.id || !lastId) return;
    const controller = new AbortController();
    resyncController = controller;
    try {
      const added = await syncMessagesAfter(session.id, lastId, {
        signal: controller.signal,
        isCurrent: () => !controller.signal.aborted
          && String(sessionStore.getSessionState().currentSession?.id || '') === String(session.id),
        // AFV-006：同步失败不再静默；30 秒内最多通知一次，避免后台重试刷屏。
        onSyncError: (err) => {
          if (controller.signal.aborted || !onSyncError) return;
          if (now() - lastSyncErrorNotifiedAt < 30000) return;
          lastSyncErrorNotifiedAt = now();
          onSyncError(err);
        },
      });
      if (!controller.signal.aborted && added > 0) updateTokenCounter();
    } finally {
      if (resyncController === controller) resyncController = null;
    }
  }

  function scheduleResync() {
    if (streaming || documentRef.visibilityState === 'hidden') return;
    if (now() - lastResyncAt < 5000) return;
    lastResyncAt = now();
    if (timer !== null) clearTimeoutFn(timer);
    timer = setTimeoutFn(() => { runResync().catch(() => {}); }, 100);
  }

  function updateStreamState(isStreaming) {
    streaming = isStreaming;
    if (stopButton) stopButton.hidden = !isStreaming;
    if (sendButton) {
      sendButton.hidden = isStreaming;
      sendButton.disabled = isStreaming;
    }
    if (isStreaming) return cancelResync();
    scheduleResync();
    const session = sessionStore.getSessionState().currentSession;
    if (session?.id) {
      artifactFacade.loadArtifacts(session.id);
      runFacade.loadRuns(session.id);
    }
  }

  function handleStop() {
    const sessionId = sessionStore.getSessionState().currentSession?.id;
    stopActiveStream();
    if (sessionId) publishAgentEvent(sessionId, { event: 'stream_cancelled' });
  }
  function handleVisibilityChange() {
    if (documentRef.visibilityState === 'visible') scheduleResync();
    else { cancelResync(); lastResyncAt = 0; }
  }
  function handlePageHide() { cancelResync(); lastResyncAt = 0; stopActiveStream(); }
  function handlePageShow(event) { if (event?.persisted) scheduleResync(); }

  function bind() {
    if (bound) return;
    stopButton?.addEventListener?.('click', handleStop);
    documentRef.addEventListener?.('visibilitychange', handleVisibilityChange);
    windowRef.addEventListener?.('online', scheduleResync);
    windowRef.addEventListener?.('pagehide', handlePageHide);
    windowRef.addEventListener?.('pageshow', handlePageShow);
    unsubscribe = onStreamStateChange(updateStreamState);
    bound = true;
  }
  function destroy() {
    if (!bound) return;
    cancelResync();
    stopButton?.removeEventListener?.('click', handleStop);
    documentRef.removeEventListener?.('visibilitychange', handleVisibilityChange);
    windowRef.removeEventListener?.('online', scheduleResync);
    windowRef.removeEventListener?.('pagehide', handlePageHide);
    windowRef.removeEventListener?.('pageshow', handlePageShow);
    unsubscribe?.();
    bound = false;
  }
  return { bind, destroy, scheduleResync, cancelResync, updateStreamState };
}
